import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import BoardCardDrawer from './BoardCardDrawer';
import ShortListCardDrawer from './ShortLists/ShortListCardDrawer';
import CustomFieldFormMappingDrawer from '../forms/CustomFieldFormMappingDrawer/CustomFieldFormMappingDrawer';
import FormResponder from '../forms/FormResponder';
import { closeFormDrawer } from './state/boards.actions';

export default function BoardCardDrawerStack({
  boardId,
  ourBoard,
  cardType,
  userBoardPermissions,
  onClose,
  getContainer,
  onlyRenderWithBoardId = false,
}) {
  const dispatch = useDispatch();

  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const {
    visible: formDrawerVisible = false,
    assignedForm,
    formData,
    cardId: formCardId,
    isEdit: selectedFormIsEdit,
    fileMap,
    ids: preloadedIds,
  } = useSelector((state) => state.boards.formDrawerConfig);
  const {
    visible: formMapDrawerVisible = false,
    selectedCardId: formMapCardId,
  } = useSelector((state) => state.boards.formMapDrawerConfig);

  const assignedDraft = useMemo(() => {
    const relevantDraftId = latestManualDraftId ?? assignedForm?.draftId;
    if (!assignedForm) return { draftId: relevantDraftId };
    return {
      ...assignedForm,
      draftId: relevantDraftId,
    };
  }, [assignedForm, latestManualDraftId]);

  const hideFormDrawer = useCallback(() => dispatch(closeFormDrawer()), [dispatch]);

  if (onlyRenderWithBoardId && !boardId) return null;

  return (
    <>
      <BoardCardDrawer
        boardId={boardId}
        cardTypeId={ourBoard?.cardTypeId}
        divisions={ourBoard?.divisions}
        showCardNumber={ourBoard?.showCardNumber}
        useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
        userBoardPermissions={userBoardPermissions}
        getContainer={getContainer}
        onClose={onClose}
      />
      <CustomFieldFormMappingDrawer
        visible={formMapDrawerVisible}
        selectedProfileId={formMapCardId}
        profileTypeId={ourBoard?.cardTypeId}
        divisions={ourBoard?.divisions}
        type="card"
      />
      <FormResponder
        visible={formDrawerVisible}
        onClose={hideFormDrawer}
        assignedForm={assignedDraft}
        preloadedData={formData}
        preloadedIds={preloadedIds}
        isOnEditStep={selectedFormIsEdit}
        cardId={formCardId}
        initialFileMap={fileMap}
      />
      <ShortListCardDrawer />
    </>
  );
}

BoardCardDrawerStack.propTypes = {
  boardId: PropTypes.string,
  ourBoard: PropTypes.shape({
    cardTypeId: PropTypes.number,
    divisions: PropTypes.arrayOf(PropTypes.string),
    showCardNumber: PropTypes.number,
  }),
  cardType: PropTypes.shape({
    useCardNumberAsCardTitle: PropTypes.bool,
  }),
  userBoardPermissions: PropTypes.shape({}),
  onClose: PropTypes.func,
  getContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onlyRenderWithBoardId: PropTypes.bool,
};

BoardCardDrawerStack.defaultProps = {
  boardId: null,
  ourBoard: {},
  cardType: {},
  userBoardPermissions: {},
  onClose: null,
  getContainer: false,
  onlyRenderWithBoardId: false,
};
